import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";
import About from "./About";
import omorelogo from "../Images/omorelogo.png";
import { addDoc, collection, doc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { getUserId } from "../utils/userId";
import { db } from "../firebase";
import { getUserName } from "../utils/getUserName";

export default function Intro() {
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [isPressing, setIsPressing] = useState(false);

  // New state for the cinematic loader
  const [isLoading, setIsLoading] = useState(() => {
    return !sessionStorage.getItem("hasLoaded");
  });
  const timerRef = useRef(null);
  const HOLD_DURATION = 800;

  // 1. Logic to hide loader after 2.5 seconds
  useEffect(() => {
    if (isLoading && isFullscreen) {
      const loadTimer = setTimeout(() => {
        setIsLoading(false);
        sessionStorage.setItem("hasLoaded", "true");
      }, 2500);
      return () => clearTimeout(loadTimer);
    }
  }, [isLoading, isFullscreen])

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isFullscreen]);

  const handlePressStart = (e) => {
    if (e.type === 'touchstart') e.preventDefault();
    setIsPressing(true);
    timerRef.current = setTimeout(() => {
      setIsFullscreen((prev) => !prev);
      setIsPressing(false);
      // Reset loader if they go back into fullscreen later
      if (!isFullscreen) setIsLoading(true);
    }, HOLD_DURATION);
  };

  const handlePressEnd = () => {
    setIsPressing(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

const saveGameStart = async () => {
  try {

    const userId = getUserId();
    const userName = getUserName();

    const gameId = "neuralis"; // same id used in Play

    await setDoc(
      doc(
        db,
        "game_sessions",
        userId,
        "sessions",
        gameId
      ),
      {
        game: gameId,
        userId,
        userName,
        lastStartTime: serverTimestamp(),
        startCount: increment(1),
 

      },
      { merge: true } // ✅ update only
    );

  } catch (e) {
    console.error(e);
  }
};


  return (
    <>
      <Helmet>

        <html lang="en" />

        <title>
          OMORE Games – Neuralis Reaction Time Test, Brain Training & Reflex Games Online
        </title>

        <meta
          name="description"
          content="OMORE Games is an independent browser game studio creating brain training games, reaction time tests, and reflex challenges. Play Neuralis online to test focus, memory, and speed."
        />

        <meta
          name="keywords"
          content="
    omore games,
    neuralis,
    neuralis game,
    reaction time test,
    reflex test online,
    brain training game,
    memory game online,
    focus game,
    reflex speed test,
    cognitive training,
    free brain games,
    browser games,
    reaction speed game,
    online reflex game
    "
        />

        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow, max-image-preview:large" />

        <meta name="author" content="OMORE Games" />

        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <meta name="theme-color" content="#0d0e10" />

        <link rel="canonical" href="https://omore.games/" />

        <link rel="icon" href="/favicon.ico" />



        {/* OPEN GRAPH */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="OMORE Games | Brain Training & Reflex Test Games" />
        <meta
          property="og:description"
          content="Play Neuralis and other brain training games. Test reaction time, focus, and memory online."
        />
        <meta property="og:url" content="https://omore.games/" />
        <meta property="og:site_name" content="OMORE Games" />

        <meta
          property="og:image"
          content="https://omore.games/og-cover.png"
        />



        {/* TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OMORE Games | Neuralis Reflex Game" />
        <meta
          name="twitter:description"
          content="Fast brain training and reaction time tests playable instantly in browser."
        />
        <meta
          name="twitter:image"
          content="https://omore.games/og-cover.png"
        />



        {/* ORGANIZATION SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "OMORE Games",
            url: "https://omore.games",
            logo: "https://omore.games/logo.png",
            founder: {
              "@type": "Person",
              name: "Omkar More"
            },
            sameAs: [
              "https://omore.games"
            ]
          })}
        </script>



        {/* WEBSITE SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "OMORE Games",
            url: "https://omore.games",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://omore.games/?q={search_term}",
              "query-input": "required name=search_term"
            }
          })}
        </script>



        {/* GAME SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoGame",
            name: "Neuralis",
            url: "https://omore.games",
            genre: "Brain Game",
            applicationCategory: "Game",
            gamePlatform: "Web Browser",
            operatingSystem: "Any",
            inLanguage: "en",
            creator: {
              "@type": "Organization",
              name: "OMORE Games"
            },
            author: {
              "@type": "Person",
              name: "Omkar More"
            },
            description:
              "Neuralis is a reflex, focus, and memory training game created by OMORE Games."
          })}
        </script>



        {/* BREADCRUMB */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://omore.games"
              }
            ]
          })}
        </script>

      </Helmet>

      <div className={isFullscreen
        ? "fixed inset-0 z-50 bg-[#0a0a0b] overflow-hidden flex flex-col select-none"
        : "relative w-full bg-transparent"
      }>

        {/* --- TIER 0: CINEMATIC STUDIO LOADER --- */}
        {isFullscreen && isLoading && (
          <div className="absolute inset-0 z-[200] bg-gradient-to-b from-[#0a0a0b] to-[#050506] flex flex-col items-center justify-center transition-opacity duration-1000">

            {/* Background Glow */}
            <div className="absolute w-[60vw] h-[60vw] bg-blue-600/5 blur-[100px] rounded-full opacity-70" />

            {/* CENTER CONTENT */}
            <div className="flex flex-col items-center space-y-8 animate-in fade-in zoom-in-95 duration-1000">

              {/* LOGO */}
              <div className="relative">
                <img
                  src={omorelogo}
                  alt="OMORE Logo"
                  className="h-20 md:h-32 w-auto brightness-110 drop-shadow-[0_0_25px_rgba(59,130,246,0.25)]"
                  loading="lazy"
                  decoding="async" 
                />

                <div className="absolute inset-0 bg-blue-500/20 blur-xl opacity-70 animate-pulse rounded-full" />
              </div>

              {/* TEXT */}
              <div className="text-center space-y-4">

                <h2 className="text-sm md:text-xl text-white tracking-[0.9em] md:tracking-[1.4em] uppercase font-black -mr-[0.9em] md:-mr-[1.4em]">
                  OMORE GAMES
                </h2>

                <div className="flex items-center justify-center gap-4">
                  <div className="h-[2px] w-10 bg-zinc-800" />

                  <span className="text-[12px] md:text-sm text-zinc-300 tracking-[0.6em] uppercase font-bold -mr-[0.6em]">
                    presents
                  </span>

                  <div className="h-[2px] w-10 bg-zinc-800" />
                </div>



              </div>

            </div>

            {/* BOTTOM SIGNATURE */}
            <div className="absolute bottom-12 flex flex-col items-center gap-1 opacity-50 animate-in fade-in duration-1000 delay-1000 select-none">

              <span className="text-[8px] tracking-[0.7em] text-zinc-500 uppercase">
                Created by
              </span>

              <span className="text-[11px] tracking-[0.9em] text-white/80 uppercase font-semibold -mr-[0.9em]">
                Omkar More
              </span>

            </div>

          </div>
        )}

        {/* 1. CINEMATIC OVERLAYS (Only in Fullscreen) */}
        {isFullscreen && (
          <>
            <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-50 bg-[length:100%_2px,3px_100%]" />
            <div className="absolute inset-10 border-t border-l border-white/5 w-20 h-20 pointer-events-none" />
            <div className="absolute bottom-10 right-10 border-b border-r border-white/5 w-20 h-20 pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] bg-blue-600/5 blur-[120px] rounded-full pointer-events-none" />
          </>
        )}

        {/* Wrap content in a fade transition so it appears smoothly after loading */}
        <div className={`
          relative flex flex-col items-center w-full max-w-7xl mx-auto px-6 text-center text-white transition-opacity duration-1000
          ${isFullscreen
            ? "h-[100svh] justify-between py-10 md:py-16"
            : "min-h-[85svh] justify-center py-12"
          }
          ${isLoading && isFullscreen ? "opacity-0" : "opacity-100"}
        `}>

          {/* TIER 1: TOP (Identity) */}
          <div className={`w-full flex flex-col items-center shrink-0 ${isFullscreen ? "animate-in fade-in slide-in-from-top-6 duration-1000" : ""}`}>
            {isFullscreen ? (
              <div className="flex flex-col items-center space-y-5">
                {/* 1. Logo on top (Primary Identity) */}
                <div className="flex flex-col items-center gap-2">
                  <img
                    src={omorelogo}
                    alt="Logo"
                    className="h-10 md:h-16 w-auto opacity-90"
                              loading="lazy"
                  decoding="async" 
                  />
                  <p className="text-[10px] md:text-xs text-zinc-500 tracking-[0.6em] uppercase font-bold italic">
                    presents
                  </p>
                </div>

                {/* 2. Status Badge below (Secondary Information) */}
                <div className="px-4 py-1 border border-blue-500/30 bg-blue-500/5 rounded-full">
                  <p className="text-[8px] md:text-[10px] text-blue-400 tracking-[0.5em] uppercase font-black">
                    Neural Network Online
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3 opacity-60">
                <p className="text-[11px] tracking-[0.6em] uppercase font-bold">OMORE GAMES</p>
                <span className="text-[10px] italic tracking-[0.3em]">presents</span>
              </div>
            )}
          </div>

          {/* TIER 2: CENTER (The Experience) */}
          <div className="flex flex-col items-center justify-center flex-grow w-full relative py-8">
            {isFullscreen && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
                <span className="opacity-[0.02] font-black text-[30vw] uppercase italic leading-none">NRLS</span>
              </div>
            )}

            <div className="mb-8 md:mb-12 animate-in fade-in zoom-in-90 duration-1000 z-10">
              <h1 className="text-7xl sm:text-8xl md:text-[10rem] font-black tracking-tighter leading-none text-white drop-shadow-[0_0_40px_rgba(59,130,246,0.5)]">
                NEURALIS
              </h1>
              <div className="flex items-center justify-center gap-4 mt-2">
                <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-blue-500" />
                <span className="text-[10px] md:text-xs tracking-[1em] uppercase text-blue-400 font-bold ml-[1em]">Evolution</span>
                <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-blue-500" />
              </div>
            </div>

            <div className="flex flex-col items-center space-y-8 animate-in fade-in duration-1000 delay-500 z-10">
              <div className="space-y-3">
                <h2 className="text-2xl md:text-5xl font-extralight tracking-[0.4em] uppercase text-white/90">
                  A New Beginning
                </h2>
                <p className="text-[9px] md:text-xs text-zinc-500 tracking-[0.6em] uppercase">Humanity, Redefined.</p>
              </div>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 pt-4 w-full">

                {/* PRIMARY: START BUTTON */}
                <button
               onClick={async () => {

    // GA
    ReactGA.event({
      category: "Game",
      action: "Start_Reflex_Test",
      label: "User clicked Start on Intro page",
    });

    // Firestore
    await saveGameStart();

    // Navigate
    navigate("/sync/eyes");

  }}
                  
                  
                  
                  className="group relative w-full max-w-[280px] md:w-auto px-16 py-5 md:px-24 md:py-7 bg-white text-black text-xs md:text-sm uppercase font-black transition-all hover:bg-blue-500 hover:text-white active:scale-95 overflow-hidden flex items-center justify-center"
                >
                  {/* 1. We moved the tracking here.
    2. -mr-[1.2em] pulls the word back to the right to cancel the extra space after the 't'.
  */}
                  <span className="relative z-10 tracking-[1.2em] -mr-[1.2em]">
                    Start
                  </span>

                  <div className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                </button>

                {/* SECONDARY: EXIT BUTTON */}
                <button
                  onMouseDown={handlePressStart}
                  onMouseUp={handlePressEnd}
                  onMouseLeave={handlePressEnd}
                  onTouchStart={handlePressStart}
                  onTouchEnd={handlePressEnd}
                  className="group relative w-full max-w-[280px] md:w-auto px-10 py-5 md:py-7 border border-white/10 hover:border-white/20 transition-all overflow-hidden bg-white/5 backdrop-blur-sm"
                >
                  <span className="relative z-10 text-[9px] md:text-[10px] tracking-[0.4em] uppercase font-bold text-zinc-500 group-hover:text-zinc-300 transition-colors">
                    {isPressing
                      ? "Processing..."
                      : isFullscreen ? "Hold to Exit" : "Hold for Fullscreen"}
                  </span>

                  {/* Progress Bar */}
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/5">
                    <div
                      className="h-full bg-blue-600 shadow-[0_0_10px_#2563eb] transition-all ease-linear"
                      style={{
                        width: isPressing ? "100%" : "0%",
                        transitionDuration: isPressing ? `${HOLD_DURATION}ms` : "150ms",
                      }}
                    />
                  </div>
                </button>

              </div>
            </div>
          </div>

          {/* TIER 3: BOTTOM (System Stats) */}
          <div className={`w-full flex flex-col items-center shrink-0 space-y-6 ${isFullscreen ? "animate-in fade-in slide-in-from-bottom-6 duration-1000" : ""}`}>


            <div className="text-[9px] md:text-xs text-zinc-400 tracking-[0.5em] md:tracking-[0.8em] uppercase font-black flex items-center justify-center gap-4 md:gap-8 opacity-40 hover:opacity-100 transition-opacity">
              <span>Focus</span>
              <span className="text-blue-500">•</span>
              <span>Reflex</span>
              <span className="text-blue-500">•</span>
              <span>Memory</span>
            </div>

            {isFullscreen && (
              <div className="flex items-center justify-center gap-6 md:gap-12 text-[8px] text-zinc-600 tracking-[0.4em] uppercase font-bold">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-blue-500/40">Latency</span>
                  <span>0.00ms</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-blue-500/40">Stability</span>
                  <span>100%</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-blue-500/40">Core</span>
                  <span>Active</span>
                </div>
              </div>
            )}

            {!isFullscreen && (
              <div className="mt-6 animate-bounce opacity-20">
                <div className="w-px h-12 bg-gradient-to-b from-blue-500 to-transparent mx-auto" />
              </div>
            )}
          </div>

          {/* MINIMAL EXIT TRIGGER */}

        </div>
      </div>

      {!isFullscreen && <About />}
    </>
  );
}